import { Bell, BellOff, Loader2, Smartphone, AlertTriangle, Share, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { usePushNotifications } from '../hooks/usePushNotifications';

const PushNotificationSettings = () => {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    toggleSubscription,
    isIOS,
    isStandalone,
    canEnablePushOnIOS
  } = usePushNotifications();

  // iOS fora do modo PWA: mostrar instruções de instalação
  if (isIOS && !isStandalone) {
    return (
      <Card className="border" style={{ borderColor: '#EAE6DF', borderRadius: '1rem' }}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" style={{ color: '#4A6B5D' }} />
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
              Notificações Push - iPhone/iPad
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEF9F3' }}>
              <p className="text-sm font-medium" style={{ color: '#D4A373' }}>
                Para receber notificações no iPhone/iPad:
              </p>
              <ol className="text-xs mt-2 space-y-2" style={{ color: '#92400E' }}>
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>Toque no ícone <Share className="h-4 w-4 inline" /> (Compartilhar) na barra do Safari</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>Selecione <PlusSquare className="h-4 w-4 inline" /> "Adicionar à Tela Inicial"</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  <span>Abra o app pela tela inicial e ative as notificações aqui</span>
                </li>
              </ol>
            </div>
            <p className="text-xs" style={{ color: '#5C6B64' }}>
              Requer iOS 16.4 ou superior
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isSupported) {
    return (
      <Card className="border" style={{ borderColor: '#EAE6DF', borderRadius: '1rem' }}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BellOff className="h-5 w-5" style={{ color: '#9CA3AF' }} />
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
              Notificações Push
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
            <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: '#D97706' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                Navegador não suportado
              </p>
              <p className="text-xs mt-1" style={{ color: '#B45309' }}>
                Seu navegador não suporta notificações push. Tente usar Chrome, Firefox ou Edge.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border" style={{ borderColor: '#EAE6DF', borderRadius: '1rem' }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" style={{ color: '#4A6B5D' }} />
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
              Notificações Push
            </CardTitle>
          </div>
          {isSubscribed && (
            <Badge style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
              Ativo
            </Badge>
          )}
        </div>
        <CardDescription>
          Receba alertas no celular quando pacientes chegarem
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {permission === 'denied' ? (
          <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
            <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: '#DC2626' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#991B1B' }}>
                Permissão bloqueada
              </p>
              <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
                Você bloqueou as notificações. Para ativar, acesse as configurações do navegador e permita notificações para este site.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="push-toggle" className="text-sm font-medium" style={{ color: '#2C3632' }}>
                  Ativar notificações
                </Label>
                <p className="text-xs" style={{ color: '#5C6B64' }}>
                  Alerta quando paciente mudar para "Aguardando"
                </p>
              </div>
              <Switch
                id="push-toggle"
                checked={isSubscribed}
                onCheckedChange={toggleSubscription}
                disabled={loading}
              />
            </div>

            {!isSubscribed && (
              <Button
                onClick={toggleSubscription}
                disabled={loading}
                className="w-full"
                style={{ backgroundColor: '#4A6B5D' }}
                data-testid="btn-ativar-push"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Bell className="h-4 w-4 mr-2" />
                )}
                Ativar Notificações
              </Button>
            )}

            {isSubscribed && (
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4" style={{ color: '#16A34A' }} />
                  <p className="text-sm" style={{ color: '#166534' }}>
                    Você receberá alertas quando seus pacientes chegarem na clínica!
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
